import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { useProviderBase } from '@/features/provider-directory/hooks/useProviderSearch';
import { getProviderDisplayName } from '@/features/provider-directory/utils/formatters';
import { fetchReferralLink } from '@/shared/api/referralLinkApi';
import { useShareContactCard } from '../hooks/useShareContactCard';
import { copyContactCard } from '../utils/contactCardClipboard';
import { recordContactCardCopy } from '../api/contactCardOpenApi';
import { ContactCardPreview } from './ContactCardPreview';
import { RecipientFields, type RecipientDraft } from './RecipientFields';
import type { ShareContactCardProvider } from './shareContactCardProvider';

export type { ShareContactCardProvider };

type ShareContactCardModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ServiceProvider profile id. The only thing the send actually needs. */
  serviceProviderId: string;
  /**
   * Already-loaded card fields, when the calling surface happens to have them.
   * Omit it and the modal fetches its own: result cards carry an id and a name
   * but no phone, email or title, and making every surface thread those through
   * would put the burden of knowing what a card contains on callers that do not
   * care.
   */
  provider?: ShareContactCardProvider;
};

type Step = 'form' | 'confirm' | 'sent';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_RECIPIENT: RecipientDraft = { email: '', firstName: '' };

/** Same fill as ReferralModal / SendAssessmentModal — shadcn `primary` is navy. */
const GREEN = 'bg-[#026857] hover:bg-[#025247]';

/**
 * Sends a Service Provider's contact card to someone who is not on bQuest.
 *
 * Three steps rather than one: the confirm step exists because a mistyped
 * address mails a provider's phone number and email to a stranger and cannot be
 * unwound. It shows the address back rather than asking for it twice — retyping
 * is the friction people defeat by pasting.
 */
export function ShareContactCardModal({
  open,
  onOpenChange,
  serviceProviderId,
  provider: knownProvider,
}: ShareContactCardModalProps) {
  // Only fetched when the caller did not supply the fields, and only while open —
  // a closed modal on a list of result cards should cost nothing.
  const { data: fetched } = useProviderBase(serviceProviderId, open && !knownProvider);

  const provider: ShareContactCardProvider = knownProvider ?? {
    id: serviceProviderId,
    displayName: fetched
      ? getProviderDisplayName(fetched.partnerCompany, fetched.companyName, fetched.user)
      : '',
    companyName: fetched?.companyName,
    professionalTitle: fetched?.professionalTitle,
    phoneNumber: fetched?.phoneNumber,
    email: fetched?.user?.email,
    profilePhoto: fetched?.user?.profilePhoto,
  };

  const [step, setStep] = useState<Step>('form');
  const [recipient, setRecipient] = useState<RecipientDraft>(EMPTY_RECIPIENT);
  const [errors, setErrors] = useState<Partial<Record<keyof RecipientDraft, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { mutate: shareCard, isPending } = useShareContactCard({
    onSuccess: () => setStep('sent'),
    onError: (error) => {
      setApiError(error?.message || 'The contact card could not be sent. Please try again.');
      setStep('form');
    },
  });

  // Reopening should not show the previous recipient, or a stale success screen.
  useEffect(() => {
    if (!open) return;
    setStep('form');
    setRecipient(EMPTY_RECIPIENT);
    setErrors({});
    setApiError(null);
    setCopied(false);
  }, [open]);

  const validate = (): boolean => {
    const next: Partial<Record<keyof RecipientDraft, string>> = {};
    if (!recipient.email.trim()) {
      next.email = 'Enter the recipient’s email address.';
    } else if (!EMAIL_PATTERN.test(recipient.email.trim())) {
      next.email = 'That does not look like a valid email address.';
    }
    if (!recipient.firstName.trim()) {
      next.firstName = 'Enter the recipient’s first name.';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleReview = () => {
    setApiError(null);
    if (validate()) setStep('confirm');
  };

  const handleCopy = async () => {
    setApiError(null);

    // Fetched here rather than when the modal opens: prefetching races the
    // click, and a fast copy would silently produce a link with no attribution.
    // A failure drops the attribution but never blocks the copy.
    const referralCode = await fetchReferralLink()
      .then((r) => r.referralCode)
      .catch(() => undefined);
    // The clipboard is the sender's own outbox: no recipient, so none of the
    // form validation applies, and no email is sent.
    try {
      await copyContactCard({
        displayName: provider.displayName,
        companyName: provider.companyName,
        professionalTitle: provider.professionalTitle,
        phoneNumber: provider.phoneNumber,
        email: provider.email,
        profileUrl: buildCopyProfileUrl(serviceProviderId, referralCode),
      });
      setCopied(true);
      // After the clipboard write, so a blocked copy is not counted as one.
      void recordContactCardCopy(serviceProviderId);
    } catch {
      setApiError('Could not copy the card. Your browser blocked clipboard access.');
    }
  };

  const handleSend = () => {
    shareCard({
      serviceProviderId,
      recipientEmail: recipient.email.trim(),
      recipientFirstName: recipient.firstName.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-x-hidden overflow-y-auto [&>*]:min-w-0 [&>*]:max-w-full">
        <DialogHeader className="min-w-0 pr-6">
          <DialogTitle className="text-balance leading-snug">
            {step === 'sent' ? 'Contact card sent' : `Share ${provider.displayName}’s contact card`}
          </DialogTitle>
          <DialogDescription>
            {step === 'form' &&
              'They do not need a bQuest account. The email includes a contact file they can save straight to their phone.'}
            {step === 'confirm' && 'Check the address before sending — this cannot be undone.'}
            {step === 'sent' && `We emailed the card to ${recipient.email.trim()}.`}
          </DialogDescription>
        </DialogHeader>

        {step !== 'sent' && <ContactCardPreview provider={provider} />}

        {apiError && (
          <Alert variant="destructive" className="min-w-0">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>{apiError}</AlertDescription>
          </Alert>
        )}

        {step === 'form' && (
          <div className="min-w-0 space-y-4">
            <RecipientFields value={recipient} onChange={setRecipient} errors={errors} />

            <div className="border-t pt-4">
              <p className="text-sm text-gray-600">
                Rather send it yourself? Copy the details and paste them into your own email.
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={handleCopy}
              >
                <Copy className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                {copied ? 'Copied' : 'Copy contact card'}
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <Alert className="min-w-0">
            <AlertCircle className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              Sending to <strong className="break-all">{recipient.email.trim()}</strong>. Their
              contact details go to whoever owns that address.
            </AlertDescription>
          </Alert>
        )}

        {step === 'sent' && (
          <Alert className="min-w-0">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            <AlertDescription>
              They can save {provider.displayName} straight to their contacts from the attachment.
            </AlertDescription>
          </Alert>
        )}

        <DialogFooter className="min-w-0">
          {step === 'form' && (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" className={GREEN} onClick={handleReview}>
                Continue
              </Button>
            </>
          )}
          {step === 'confirm' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('form')}
                disabled={isPending}
              >
                Back
              </Button>
              <Button type="button" className={GREEN} onClick={handleSend} disabled={isPending}>
                {isPending ? 'Sending…' : 'Send card'}
              </Button>
            </>
          )}
          {step === 'sent' && (
            <Button type="button" className={GREEN} onClick={() => onOpenChange(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The link that goes into the copied card.
 *
 * The provider's profile — the same destination the emailed CTA uses, and the
 * one that leads to signup now that the account gate is a modal on the page
 * rather than a separate `/register` route. No `share` parameter: nothing was
 * delivered, so there is no delivery to attribute a visit back to.
 */
const buildCopyProfileUrl = (serviceProviderId: string, referralCode?: string): string => {
  const base = `${window.location.origin}/providers/${serviceProviderId}`;
  return referralCode ? `${base}?refCode=${encodeURIComponent(referralCode)}` : base;
};
